import { createContext, useContext, useReducer, type ReactNode } from 'react';

interface LoginModalState {
  showLoginDialog: boolean;
  returnPath?: string;
}

type LoginModalAction =
  | { type: 'OPEN_LOGIN_DIALOG'; returnPath?: string }
  | { type: 'CLOSE_LOGIN_DIALOG' };

interface LoginModalContextType {
  state: LoginModalState;
  openLoginDialog: (returnPath?: string) => void;
  closeLoginDialog: () => void;
}

const initialState: LoginModalState = {
  showLoginDialog: false,
};

const loginModalReducer = (
  state: LoginModalState,
  action: LoginModalAction,
): LoginModalState => {
  switch (action.type) {
    case 'OPEN_LOGIN_DIALOG':
      return {
        ...state,
        showLoginDialog: true,
        returnPath: action.returnPath,
      };
    case 'CLOSE_LOGIN_DIALOG':
      return {
        ...state,
        showLoginDialog: false,
        returnPath: undefined,
      };
    default:
      return state;
  }
};

const LoginModalContext = createContext<LoginModalContextType | undefined>(
  undefined,
);

interface LoginModalProviderProps {
  children: ReactNode;
}

export const LoginModalProvider = ({ children }: LoginModalProviderProps) => {
  const [state, dispatch] = useReducer(loginModalReducer, initialState);

  const openLoginDialog = (returnPath?: string) => {
    dispatch({ type: 'OPEN_LOGIN_DIALOG', returnPath });
  };

  const closeLoginDialog = () => {
    dispatch({ type: 'CLOSE_LOGIN_DIALOG' });
  };

  const value: LoginModalContextType = {
    state,
    openLoginDialog,
    closeLoginDialog,
  };

  return (
    <LoginModalContext.Provider value={value}>
      {children}
    </LoginModalContext.Provider>
  );
};

export const useLoginModal = (): LoginModalContextType => {
  const context = useContext(LoginModalContext);

  if (context === undefined) {
    throw new Error('useLoginModal must be used within a LoginModalProvider');
  }

  return context;
};
